import React, { useState, useRef,useEffect } from "react";
import Header from "./common/Header";
import Footer from "./common/Footer";
import logo from "./img/quanto_light.png";
import "./style/UnlockProfit.css"; // Correct usage of UnlockProfit.css
import stockrot1 from "./img/unlock-profit/stock-rotation/stock_rotation_1.png";
import stockrot2 from "./img/unlock-profit/stock-rotation/stock_rotation_2.png";
import stockrot3 from "./img/unlock-profit/stock-rotation/stock_rotation_3.png";
import stockrot4 from "./img/unlock-profit/stock-rotation/stock_rotation_4.png";
import { motion, AnimatePresence } from "framer-motion";
import {
  RefreshCw,
  ShoppingCart,
  Package,
  BarChart,
  Zap,
  Layers,
  Shield,
  Smartphone,
  ChevronRight,
} from "lucide-react";
import './style/products/quantofyP.css';
import multi1 from "./img/unlock-profit/multi/multi1.png";
import multi2 from "./img/unlock-profit/multi/multi2.png";
import multi3 from "./img/unlock-profit/multi/multi3.png";
import multi4 from "./img/unlock-profit/multi/multi4.png";

import crm1 from "./img/unlock-profit/crm/crm1.png";
import crm2 from "./img/unlock-profit/crm/crm2.png";
import crm3 from "./img/unlock-profit/crm/crm3.png";
import crm4 from "./img/unlock-profit/crm/crm4.png";

import sale1 from "./img/unlock-profit/sale/sale1.png";
import sale2 from "./img/unlock-profit/sale/sale2.png";
import sale3 from "./img/unlock-profit/sale/sale3.png";
import sale4 from "./img/unlock-profit/sale/sale4.png";

import rev1 from "./img/unlock-profit/rev/rev1.PNG";
import rev2 from "./img/unlock-profit/rev/rev2.png";
import rev3 from "./img/unlock-profit/rev/rev3.png";
import rev4 from "./img/unlock-profit/rev/rev4.jpg";


const sectionsData = [
  {
    id: 1,
    shortTitle: "Stock Rotation",
    title: "Increase your Stock Rotation",
    points: [
      "Classify your inventory using industry-standard attributes like category, size, brand, and demand patterns for smarter stock management, better forecasting, and improved efficiency.",
      "Track and analyze the aging of your stock to identify slow-moving or obsolete items, enabling timely decisions to optimize inventory, reduce holding costs, and improve cash flow.",
      "Streamline your inventory management by automating the re-filling process, ensuring optimal stock levels and reducing the risk of overstocking or stockouts.",
      "Design and implement dynamic sales promotion schemes tailored to customer preferences, market trends, and inventory levels, driving increased engagement and boosting sales.",
    ],
    images: [
      stockrot1,
      stockrot2,
      stockrot3,
      stockrot4,
    ],
    color: "#22c55e",
  },
  {
    id: 2,
    shortTitle: "Multi Stores",
    title: "Scale up to Multi Stores",
    points: [
      "Adopt a centralized platform to efficiently manage inventory, sales, reporting, and other key operations in real-time, ensuring seamless coordination, accurate insights, and improved decision-making across your business.",
      "Optimize stock levels by seamlessly transferring inventory across locations, ensuring products are available where needed, reducing stockouts, and enhancing overall supply chain efficiency.",
      "Efficiently manage centralized customer loyalty programs and track valuable data, enabling personalized offers, boosting customer retention, and driving repeat business across all channels.",
      "Generate consolidated or store-wise comparison reports directly from the head office, providing real-time insights into performance, sales trends, and inventory status across all locations for better decision-making.",
    ],
    images: [
      multi1,
      multi2,
      multi3,
      multi4,
    ],
    color: "#6366f1",
  },
  {
    id: 3,
    shortTitle: "Customer CRM",
    title: "Master Customer Relationship Management",
    points: [
      "Centralize customer data in one platform to gain a 360-degree view of their preferences, purchase history, and behavior, enabling personalized marketing, improved customer service, and better retention strategies.",
      "Analyze customer buying patterns and preferred price ranges to tailor product offerings, optimize pricing strategies, and create targeted promotions, driving higher sales and enhancing customer satisfaction.",
      "Launch both targeted and mass marketing campaigns with precision, leveraging customer data and insights to create personalized messages or wide-reaching promotions, maximizing engagement and driving sales growth.",
      "Capture valuable customer feedback through surveys and reviews, then use the insights to improve products and services. Foster customer loyalty by addressing their needs and offering personalized experiences to enhance retention.",
    ],
    images: [
      crm1,
      crm2,
      crm3,
      crm4,
    ],
    color: "#ec4899",
  },
  {
    id: 4,
    shortTitle: "Sales Promotion",
    title: "Unlock Sales Promotion Offers",
    points: [
      "Attract and retain customers by offering dynamic sales promotions tailored to their preferences, encouraging repeat purchases and increasing customer loyalty while boosting overall sales performance.",
      "Efficiently manage multiple promotional offers such as Buy 1 Get 1, combo deals, coupons, and free gifts, ensuring seamless execution, tracking, and reporting to maximize customer engagement and sales.",
      "Enable multiple offers on the same barcode without the need for generating new ones, simplifying promotions and ensuring a seamless customer experience while maintaining accurate inventory and pricing.",
      "Apply offers based on specific products, attributes, or sections, allowing for targeted promotions that cater to different customer segments and enhance sales in key areas of your inventory.",
    ],
    images: [
      sale1,
      sale2,
      sale3,
      sale4,
    ],
    color: "#f59e0b",
  },
  {
    id: 5,
    shortTitle: "Revenue Leakage",
    title: "Avoid Revenue Leakage - The Silent Profit Killer",
    points: [
      "Restrict unauthorized additional discounts at the POS by setting clear rules and permissions, ensuring pricing integrity, preventing margin erosion, and maintaining consistent profitability across all transactions.",
      "Avoid additional GST payments by offering cash discounts instead of price reductions, ensuring tax compliance while providing savings to customers and maintaining your profit margins.",
      "Implement image-based sales returns to easily identify and track your own stock, ensuring accurate returns processing, reducing errors, and maintaining inventory integrity.",
      "APrevent revenue leakage by ensuring correct margins are applied at every transaction, maintaining accurate pricing and profitability while minimizing the risk of errors or unauthorized discounts.",
    ],
    images: [
      rev1,
      rev2,
      rev3,
      rev4,
    ],
    color: "#ef4444",
  },
];

const features = [
  { title: "Increase your Stock Rotation", description: "For Quanto-integrated clients, Quantofyy syncs inventory, orders, and customer data automatically, ensuring accuracy and up-to-date information across platforms.", icon: <RefreshCw />, image: 'retailquantofy.png' },
  { title: "Scale up to Multi Stores", description: "Quantofyy is designed for both Quanto users and retail businesses across the industry, making it a versatile e-commerce solution that supports any retail business model.", icon: <Layers />, image: 'retailquantofy2.png' },
  { title: "Master Customer Relationship Management", description: "Manage your product catalog with ease, from descriptions and pricing to stock levels. Bulk uploads and detailed attributes help streamline large inventories.", icon: <Smartphone />, image: 'retailquantofy3.png' },
  { title: "Unlock Sales Promotion Offers", description: "Access actionable insights into customer behavior and sales performance with comprehensive analytics tools. Use customizable reports to make data-driven decisions.", icon: <ShoppingCart />, image: 'retailquantofy4.png' },
  { title: "Avoid Revenue Leakage - The Silent Profit Killer", description: "Enhance customer engagement with personalized recommendations, targeted promotions, and tailored loyalty programs to drive conversions and repeat business.", icon: <Shield />, image: 'retailquantofy5.png' },
];


function Section({ shortTitle, title, points, images, color }) {
  return (
    <div className="unlock_profit-section">
      {/* <div className="unlock_profit-headerLine" style={{ backgroundColor: color }}>
        <span className="unlock_profit-badge" style={{ backgroundColor: color }}>
          {shortTitle}
        </span>
      </div> */}
      {/* <h2 className="unlock_profit-section-title">{title}</h2> */}
      <div className="unlock_profit-bentoGrid">
        {points.map((point, index) => (
          <div key={index} className="unlock_profit-bentoItem">
            <div className="unlock_profit-imageWrapper">
              <img
                src={images[index]} // Dynamically load the corresponding image
                alt={point}
                className="unlock_profit-image"
              />
            </div>
            <p>{point}</p>
          </div>
        ))}
      </div>
    </div>
  );
}


export default function UnlockProfit() {
  const [activeFeature, setActiveFeature] = useState(0);
  const isMobile = window.innerWidth < 768;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <>
      <Header />
      <section className="unlock_profit-title-section">
        <h2>
          Unlock Profit Potential with
          <img
            alt="quantologo"
            src={logo}
            style={{ width: 280, marginLeft: 10, verticalAlign: "middle" }}
          />
        </h2>
      </section>
      <section className="unlock_profit-intro-section">
        <h1>Profit Maximized, Effort Minimized: Unlock Profit with Quanto ERP</h1>
        <p>
          Quanto ERP prevents revenue leaks, boosts stock turnover, and simplifies multi-store expansion. Use targeted promotions to increase sales and build customer loyalty, all while keeping your operations efficient and profitable.
        </p>
      </section>
      <div style={{
        width: !isMobile ? '80%' : '95%',
        margin: 'auto'
      }}>
        <div className="quantofy-features-card">
          {/* <h2 className="quantofy-section-title">Unlock Profit Potential With Quanto</h2> */}
          <div className="quantofy-features-layout">
            <div className="quantofy-feature-list" style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
            }}>
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className={`quantofy-feature-item ${activeFeature === index ? "active" : ""}`}
                  onClick={() => setActiveFeature(index)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  style={{
                    width: '100%',
                  }}
                >
                  <div className="quantofy-feature-icon">{feature.icon}</div>
                  <span className="quantofy-feature-title">{feature.title}</span>
                </motion.div>
              ))}
            </div>
            <div className="quantofy-feature-details">
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFeature}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                  className="quantofy-feature-content"
                >
                  <h4 className="quantofy-feature-detail-title">{features[activeFeature].title}</h4>
                  {/* <p className="quantofy-feature-description"</main>>{features[activeFeature].description}</p> */}
                  {/* <img
                    src={require(`./img/product-quantofy/${features[activeFeature].image}`)}
                    alt={features[activeFeature].title}
                    className="quantofy-feature-image"
                  /> */}
                  {/* <button className="quantofy-learn-more-btn">
                    Learn More
                    <ChevronRight className="quantofy-btn-icon" />
                    </button> */}
                  <main className="unlock_profit-main-container">
                    {/* {sectionsData.map((section) => (
                      <Section key={section.id} {...section} />
                    ))} */}
                    <Section key={sectionsData[activeFeature].id} {...sectionsData[activeFeature]} />
                  </main>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}
